import { useWorksheetAPI } from '@/composables/WorksheetAPI';

// Caps how many worksheets stay loaded per classification tab. Without this, "load more"
// only ever grows the list, which is what made iOS scrolling degrade over a session.
const MAX_LOADED_WORKSHEETS = 50;

export const useWorksheetOverviewStore = defineStore('worksheetOverview', () => {
  const userConfigurationsStore = useUserConfigurationStore();
  const tokenStore = useTokenStore();
  const worksheetAPI = useWorksheetAPI();
  const activeTab = ref<WorksheetClassification | undefined>(undefined);

  const allWorksheets = ref<Map<number, WorksheetListResponseDTO>>(new Map());

  type WorksheetList = {
    [key in WorksheetClassification]: WorksheetListResponseContainerDTO
  };

  const worksheets = reactive<WorksheetList>({
    [WorksheetClassification.Mine]: <WorksheetListResponseContainerDTO>{},
    [WorksheetClassification.Shared]: <WorksheetListResponseContainerDTO>{},
    [WorksheetClassification.ResponsibleFor]: <WorksheetListResponseContainerDTO>{},
    [WorksheetClassification.All]: <WorksheetListResponseContainerDTO>{},
    [WorksheetClassification.MineCompleted]: <WorksheetListResponseContainerDTO>{}
  });

  const loadedSegments = ref(new Set<WorksheetClassification>());

  const classificationMap: Record<WorksheetClassification, ApiWorksheetClassification> = {
    Mine: ApiWorksheetClassification.Mine,
    MineCompleted: ApiWorksheetClassification.Mine,
    ResponsibleFor: ApiWorksheetClassification.ResponsibleFor,
    Shared: ApiWorksheetClassification.Shared,
    All: ApiWorksheetClassification.All
  };

  function insertWorksheetSorted(classification: WorksheetClassification, worksheet: WorksheetListResponseDTO) {
    worksheets[classification].ServerCount += 1;

    // If list isn't loaded, we don't need to insert, as list will be loaded upon entering
    if (!loadedSegments.value.has(classification)) {
      return;
    }

    const worksheetDate = new Date(worksheet.WorksheetStartDate);
    const spliceIndex = worksheets[classification].Worksheets.findIndex(w => new Date(w.WorksheetStartDate) > worksheetDate);

    if (spliceIndex !== -1) {
      worksheets[classification].Worksheets.splice(spliceIndex, 0, worksheet);
    } else {
      // No worksheets are newer, append to end
      worksheets[classification].Worksheets.push(worksheet);
    }
  }

  function removeWorksheet(classification: WorksheetClassification, worksheet: WorksheetListResponseDTO) {
    worksheets[classification].ServerCount -= 1;

    // If list isn't loaded, we don't need to remove, as list will be loaded upon entering
    if (!loadedSegments.value.has(classification)) {
      return;
    }

    const spliceIndex = worksheets[classification].Worksheets.indexOf(worksheet);

    if (spliceIndex !== -1) {
      worksheets[classification].Worksheets.splice(spliceIndex, 1);
    }
  }

  async function getWorksheets(worksheetListRequest: Omit<WorksheetListRequestDTO, 'Classification'>, dataHandling: 'replace' | 'append' | 'prepend', classification: WorksheetClassification) {

    const request: WorksheetListRequestDTO = {
      ...worksheetListRequest,
      Classification: classificationMap[classification]
    };

    const getWorksheetsResponse: ResponseDTO<WorksheetListResponseContainerDTO> | undefined = await worksheetAPI.getWorksheets(request);

    if (getWorksheetsResponse !== undefined && getWorksheetsResponse.HasErrors === false) {
      if (dataHandling === 'replace') {
        worksheets[classification].MoreFutureWorksheets = getWorksheetsResponse.Data.MoreFutureWorksheets;
        worksheets[classification].MorePastWorksheets = getWorksheetsResponse.Data.MorePastWorksheets;

        if (worksheets[classification]) {
          if (worksheets[classification].Worksheets) {
            worksheets[classification].Worksheets.length = 0;
          } else {
            worksheets[classification].Worksheets = [];
          }
        }
      }

      worksheets[classification].ServerCount = getWorksheetsResponse.Data.ServerCount;

      const newWorksheets = [];

      for (const worksheet of getWorksheetsResponse.Data.Worksheets) {
        const existing = allWorksheets.value?.get(worksheet.WorksheetID);

        if (existing) {
          Object.assign(existing, worksheet);
          newWorksheets.push(existing);
        } else {
          allWorksheets.value?.set(worksheet.WorksheetID, worksheet);
          newWorksheets.push(worksheet);
        }
      }

      const list = worksheets[classification];

      switch (dataHandling) {
        case 'prepend': {
          list.Worksheets.splice(0, 0, ...newWorksheets);
          list.MorePastWorksheets = getWorksheetsResponse.Data.MorePastWorksheets;

          // Keep the loaded window bounded so iOS doesn't have to render/scroll an
          // ever-growing list. Trim from the opposite (future) end, and flip that
          // side's flag back on since we're evicting locally, not because the
          // server ran out — the user can page back into it on demand.
          const overflow = list.Worksheets.length - MAX_LOADED_WORKSHEETS;

          if (overflow > 0) {
            list.Worksheets.splice(list.Worksheets.length - overflow, overflow);
            list.MoreFutureWorksheets = true;
          }

          break;
        }
        default: {
          list.Worksheets.splice(list.Worksheets.length, 0, ...newWorksheets);
          list.MoreFutureWorksheets = getWorksheetsResponse.Data.MoreFutureWorksheets;

          const overflow = list.Worksheets.length - MAX_LOADED_WORKSHEETS;

          if (overflow > 0) {
            list.Worksheets.splice(0, overflow);
            list.MorePastWorksheets = true;
          }

          break;
        }
      }
    }
  }

  async function getWorksheetCounts(worksheetCountsRequest: WorksheetCountsRequestDTO) {
    const getWorksheetCountsResponse: ResponseDTO<WorksheetCountsResponseDTO> | undefined = await worksheetAPI.getWorksheetCounts(worksheetCountsRequest);

    if (getWorksheetCountsResponse !== undefined && getWorksheetCountsResponse.HasErrors === false) {
      for (const [classification, count] of Object.entries(getWorksheetCountsResponse.Data.Counts) as [WorksheetClassification, number][]) {

        if (worksheets[classification]) {
          worksheets[classification].ServerCount = count;
        }
      }
    }
  }

  function moveToMine(worksheetID: number) {
    // check if there are worksheets in the lists

    // find worksheet in overview lists
    const worksheet = allWorksheets.value.get(worksheetID);

    if (worksheet) {
      const fitterName = userConfigurationsStore.config.FitterName;
      // change "fælles" to user's name
      worksheet.FitterName = fitterName;

      const sharedIndex = worksheets[WorksheetClassification.Shared].Worksheets.indexOf(worksheet);

      if (sharedIndex !== -1) {
      // remove worksheet from shared list
        removeWorksheet(WorksheetClassification.Shared, worksheet);
        insertWorksheetSorted(WorksheetClassification.Mine, worksheet);
      }
    }
  }

  const conditions = {
    'All': (worksheet: WorksheetListResponseDTO) => ![WorksheetStatus.Completed, WorksheetStatus.Closed].includes(worksheet.StatusEnum),
    'Mine': (worksheet: WorksheetListResponseDTO) => ![WorksheetStatus.Completed, WorksheetStatus.Closed].includes(worksheet.StatusEnum) && worksheet.FitterID == userConfigurationsStore.fitterId,
    'MineCompleted': (worksheet: WorksheetListResponseDTO) => worksheet.StatusEnum == WorksheetStatus.Completed && worksheet.FitterID == userConfigurationsStore.fitterId,
    'ResponsibleFor': async (worksheet: WorksheetListResponseDTO) => ![WorksheetStatus.Completed, WorksheetStatus.Closed].includes(worksheet.StatusEnum) && worksheet.ProjectManagerID == await tokenStore.getUserID(),
    'Shared': (worksheet: WorksheetListResponseDTO) => worksheet.StatusEnum !== WorksheetStatus.Closed && worksheet.FitterID == 1
  };

  async function updateWorksheets(worksheetIDs?: number[], viewWorksheets?: WorksheetViewResponseDTO[], listWorksheets?: WorksheetListResponseDTO[]) {
    if (!worksheetIDs && !viewWorksheets && !listWorksheets) {
      return;
    }

    const worksheetList = await (async () => {
      const worksheetList: WorksheetListResponseDTO[] = [];

      if (worksheetIDs && worksheetIDs.length) {
        const worksheetRequest: WorksheetListByIDsRequestDTO = {
          WorksheetIDs: worksheetIDs,
          FitterIDs: [userConfigurationsStore.fitterId]
        };
        const worksheetResponse = await worksheetAPI.getWorksheetListByIDs(worksheetRequest);

        if (!worksheetResponse?.HasErrors && worksheetResponse?.Data) {
          worksheetList.push(...worksheetResponse.Data);
        }
      }

      if (viewWorksheets?.length) {
        worksheetList.push(...viewWorksheets.map(ws => mapViewToList(ws)));
      }

      if (listWorksheets?.length) {
        worksheetList.push(...listWorksheets);
      }

      return worksheetList;
    })();

    if (!worksheetList.length) {
      return;
    }

    for (const listWorksheet of worksheetList) {
      const existing = allWorksheets.value.get(listWorksheet.WorksheetID);

      // If the worksheet is in the "primary list", update its contents, so we don't lose the object reference
      // Otherwise, insert it into the "primary list"
      if (existing) {
        Object.assign(existing, listWorksheet);
      } else {
        allWorksheets.value.set(listWorksheet.WorksheetID, listWorksheet);
      }

      const worksheet = existing ?? listWorksheet;

      Object.values(WorksheetClassification).forEach(async (key) => {
        const worksheetBelongs = key == WorksheetClassification.ResponsibleFor ? await conditions[key](listWorksheet) : conditions[key](listWorksheet);

        if (worksheetBelongs) {
          const found = worksheets[key].Worksheets?.indexOf(worksheet) !== -1;

          if (!found) { // not in list, insert
            insertWorksheetSorted(key, worksheet);
          } else if (listWorksheet.WorksheetStartDate !== worksheet.WorksheetStartDate) { // already in list, but has new startdate, reinsert
            removeWorksheet(key, worksheet);
            insertWorksheetSorted(key, worksheet);
          }
        } else { // doesn't belong, remove
          removeWorksheet(key, worksheet);
        }
      });
    }
  }

  function updateWorksheetStatus(worksheetID: number, primaryStatus: WorksheetStatus) {
    const worksheet = allWorksheets.value.get(worksheetID);

    if (worksheet) {
      const oldStatusCompletedOrClosed = [WorksheetStatus.Completed, WorksheetStatus.Closed].includes(worksheet.StatusEnum);
      const newStatusCompletedOrClosed = [WorksheetStatus.Completed, WorksheetStatus.Closed].includes(primaryStatus);

      // Update status
      worksheet.StatusEnum = primaryStatus;

      // Update lists, if necessary
      if (!oldStatusCompletedOrClosed && newStatusCompletedOrClosed) {
        for (const key of loadedSegments.value.keys()) {
          if (key !== WorksheetClassification.MineCompleted) {
            removeWorksheet(key, worksheet);
          } else if (worksheet.FitterID == userConfigurationsStore.fitterId) {
            insertWorksheetSorted(key, worksheet);
          }
        }
      } else if (oldStatusCompletedOrClosed && !newStatusCompletedOrClosed) {
        updateWorksheets([worksheetID]);
      }
    }
  }

  function mapViewToList(from: WorksheetViewResponseDTO) {
    const to: WorksheetListResponseDTO = {
      ...from,
      Description: from.WorksheetDescription,
      NextCalendarEventDate: null,
      WorkZip: from.WorkZipCode,
      WorkZipId: from.WorkZipCodeId,
      ClosedDate: from.ClosedDate ?? null,
      CustomerAddress: from.CustomerAddress ?? '',
      CustomerZip: from.CustomerZip ?? '',
      CustomerCity: from.CustomerCity ?? '',
      ProjectManagerID: from.ProjectManagerId
    };

    return to;
  }

  return {
    _worksheets: worksheets,
    worksheets: readonly(worksheets),
    getWorksheets,
    getWorksheetCounts,
    moveToMine,
    updateWorksheetStatus,
    activeTab,
    loadedSegments,
    updateWorksheets,
    allWorksheets
  };
});
